import api from "./client";
import type { InAppNotification } from "@store/inAppNotificationsStore";

/** "off" = never, "assigned" = only if stakeholder, "always" = broadcast */
export type NotifPrefValue = "off" | "assigned" | "always";

export type NotificationPreferencesPayload = {
  event_types: Record<string, NotifPrefValue> | null;
  ordre_status_filters: string[] | null;
};

export type OrgMemberPreferences = {
  user_id: string;
  display_name: string;
  photo_url: string;
  notification_preferences: NotificationPreferencesPayload;
};

export type ServerNotification = {
  id: string;
  user_id: string;
  org_id: string;
  type: string;
  title: string;
  body: string;
  href: string;
  read: boolean;
  created_at: string;
};

export type DevisAccord = {
  id: string;
  /** internet_rdv : RDV pris sur le site constructeur, à compléter dans ServiceBox.
   *  Pas de « Traité » : la carte part d'elle-même quand la synchro le voit complété. */
  type: "devis_signed" | "devis_modified" | "devis_no_response" | "internet_rdv" | "low_rating";
  created_at: string;
  ordre_id: string;
  ordre_num: string;
  client_name: string;
  amount: number | null;
  is_modification: boolean;
  /** Devis complémentaire resté sans réponse après le dernier rappel : à reprendre. */
  no_response: boolean;
  /** Genre du devis accepté — absent sur les accords d'avant ce champ. */
  devis_kind?: "initial" | "complementaire" | null;
  /** 1er devis sans réponse : heure de l'annulation programmée du RDV (« 16:00 »). */
  cancel_time?: string | null;
  /** 1er devis : « last_reminder_sent » = alerte posée à l'envoi du dernier rappel. */
  stage?: "last_reminder_sent" | "after_last_reminder" | null;
  /** Rang de la relance qui vient de partir (2 = « 2e relance »). */
  reminder_rank?: number | null;
  /** false : la relance n'a pu partir sur aucun canal. */
  delivered?: boolean | null;
  /** internet_rdv : marque du site (AP, AC…) et réception (secondes epoch). */
  internet_brand?: string | null;
  reception_seconds?: number | null;
  /** low_rating : avis négatif du client (note ≤ seuil), à rappeler. id = « rating:<ordre_id> ». */
  rating?: number | null;
  vehicle: string;
};

/** Convert server notification to the frontend InAppNotification shape. */
function toInApp(n: ServerNotification): InAppNotification {
  return {
    id: n.id,
    type: n.type as InAppNotification["type"],
    title: n.title,
    body: n.body,
    href: n.href,
    read: n.read,
    createdAt: n.created_at,
  };
}

export const notificationsApi = {
  fetchAll: async (): Promise<InAppNotification[]> => {
    const { data } = await api.get<ServerNotification[]>("/notifications");
    return data.map(toInApp);
  },

  markRead: async (id: string): Promise<void> => {
    await api.patch(`/notifications/${id}/read`);
  },

  markAllRead: async (): Promise<void> => {
    await api.post("/notifications/mark-all-read");
  },

  dismiss: async (id: string): Promise<void> => {
    await api.delete(`/notifications/${id}`);
  },

  clearAll: async (): Promise<void> => {
    await api.delete("/notifications");
  },

  // ── Accords de devis (popup partagée par l'org, jusqu'à « Traité ») ──

  listDevisAccords: async (): Promise<DevisAccord[]> => {
    const { data } = await api.get<DevisAccord[]>("/notifications/devis-accords");
    return data;
  },

  markDevisAccordTreated: async (id: string): Promise<void> => {
    await api.post(`/notifications/devis-accords/${id}/treated`);
  },

  // ── Notification preferences ──

  getPreferences: async (): Promise<NotificationPreferencesPayload> => {
    const { data } = await api.get<NotificationPreferencesPayload>("/notifications/preferences");
    return data;
  },

  updatePreferences: async (prefs: NotificationPreferencesPayload): Promise<void> => {
    await api.put("/notifications/preferences", prefs);
  },

  getOrgPreferences: async (): Promise<OrgMemberPreferences[]> => {
    const { data } = await api.get<OrgMemberPreferences[]>("/notifications/preferences/org");
    return data;
  },

  updateUserPreferences: async (userId: string, prefs: NotificationPreferencesPayload): Promise<void> => {
    await api.put(`/notifications/preferences/${userId}`, prefs);
  },
};
